import { Card, CardBody, Heading, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  EspdResponseDataType,
  type EspdRequirementResponseDto,
  type EspdResponseDto,
  type ResolvedEspdCriterionDto,
} from "../../../../../autogen/bff-api";
import { StatusAccentBar } from "../../../../../common/components/StatusAccentBar";
import type { StatusVariant } from "../../../../../common/components/StatusVariant";
import { isCriterionComplete } from "./espdCompleteness";
import type { EspdPresentationText } from "./espdPresentationText";
import { EspdRequirementGroupView } from "./EspdRequirementGroupView";
import { espdAnswerFor, withDerivedAnswers } from "./useEspdAnswers";

interface Props {
  criterion: ResolvedEspdCriterionDto;
  espd: EspdResponseDto;
  onChange: (next: EspdResponseDto) => void;
  readOnly: boolean;
  derivedAnswers?: EspdRequirementResponseDto[];
  hideOwnedFields?: boolean;
  awaitingNamedCompany?: boolean;
  presentation?: EspdPresentationText;
  showStatusBorder?: boolean;
  children?: React.ReactNode;
}

export const EspdCriterionForm = ({
  criterion,
  espd,
  onChange,
  readOnly,
  derivedAnswers,
  hideOwnedFields,
  awaitingNamedCompany,
  presentation,
  showStatusBorder,
  children,
}: Props) => {
  const { i18n } = useTranslation();
  const norsk = i18n.language.startsWith("no");
  const name =
    (norsk ? presentation?.titleText?.no : presentation?.titleText?.en) ||
    (norsk && criterion.nameNorwegian) ||
    criterion.name;
  const description = (norsk && criterion.descriptionNorwegian) || criterion.description;
  const helperText = norsk ? presentation?.helperText?.no : presentation?.helperText?.en;
  // Some EO_DATA criteria start with a structural wrapper group. Without a lead
  // indicator in the first group, later top-level groups are independently visible.
  const firstGroupLead = criterion.requirementGroups[0]?.requirements.find(
    (r) => r.responseDataType === EspdResponseDataType.Indicator,
  );
  const mainAnswerProvided = firstGroupLead ? espdAnswerFor(espd, firstGroupLead.id)?.indicator !== undefined : true;
  const complete =
    isCriterionComplete(criterion, withDerivedAnswers(espd, derivedAnswers)) && !awaitingNamedCompany;
  const variant: StatusVariant = complete ? "ok" : mainAnswerProvided ? "issue" : "neutral";

  return (
    <Card
      variant="outline"
      rounded="xl"
      position={showStatusBorder ? "relative" : undefined}
      overflow={showStatusBorder ? "hidden" : undefined}
    >
      {showStatusBorder && <StatusAccentBar variant={variant} />}
      <CardBody>
        <Heading size="sm" mb="1">
          {name}
        </Heading>
        {description && (
          <Text fontSize="sm" color="smMuted" mb="4">
            {description}
          </Text>
        )}
        {helperText && (
          <Text fontSize="sm" color="smMuted" mb="4">
            {helperText}
          </Text>
        )}
        <Stack spacing="5">
          {criterion.requirementGroups.map((g, index) => (
            <EspdRequirementGroupView
              key={g.id}
              group={g}
              espd={espd}
              onChange={onChange}
              readOnly={readOnly}
              hideOwnedFields={hideOwnedFields}
              parentAnswered={index === 0 || mainAnswerProvided}
            />
          ))}
        </Stack>
        {children}
      </CardBody>
    </Card>
  );
};
